import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, AfterViewInit, NgZone, ElementRef, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import * as L from 'leaflet';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, filter, startWith, switchMap, take } from 'rxjs/operators';
import { State } from 'src/app/store';
import { organisationSelector } from 'src/app/global/store/global.selectors';
import { profileSelector } from 'src/app/user/store/user.selectors';
import { Profile, Address } from 'src/app/user/user.model';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { Organisation } from 'src/app/global/global.model';
import { GeoidService } from 'src/app/global/components/forms/geoid-address/service/geoid.abstract.service';
import { FormControl } from '@angular/forms';
import { CategoryItem, GeoJSONLocationRequest, SubCategoryItem } from 'src/app/request-v2/models/create-request.dto';
import { RequestsV2ImplService } from 'src/app/request-v2/services/requests-v2.impl.service';
import { LocationOption, GeoJSONType, GeoJSONGeometry, RequestLocationAddress } from '../../../models/request.model';
import { createLocationMarkerIcon } from 'src/app/global/utils/map/map-marker-icon.util';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';

@Component({
    selector: 'app-location-selection',
    templateUrl: './location-selection.component.html',
    styleUrls: ['./location-selection.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationSelectionComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() selectedCategory: CategoryItem | null = null;

    @Input() selectedSubcategory: SubCategoryItem | null = null;

    @Input() locationOptions: LocationOption[] = [];

    @Input() previouslySelectedLocation: LocationOption | null = null;

    @Output() locationSelected = new EventEmitter<LocationOption>();

    @Output() nextStep = new EventEmitter<void>();

    @Output() skipLocation = new EventEmitter<void>();

    @Output() selectionStateChanged = new EventEmitter<boolean>();

    @Output() locationRequestData = new EventEmitter<GeoJSONLocationRequest>();

    @ViewChild('mapContainer', { static: false }) mapContainer?: ElementRef;

    // --- Component State ---
    locationSearchQuery = '';

    searchControl = new FormControl();

    addressSuggestions: Observable<Address[]>;

    currentlySelectedLocation: LocationOption | null = null;

    selectedAddress: RequestLocationAddress | null = null;

    hasLocationSelected = false;

    mapLoading = true;

    mapInitialized = false;

    mapError = false;

    displayLocationOptions: LocationOption[] = [];

    entireStreet = false;

    // --- Leaflet Map Related Properties ---
    private map: L.Map | null = null;

    private marker: L.Marker | null = null;

    // Using organization center as fallback if no better option is available
    private fallbackLocation: [number, number] = [45.5017, -73.5673]; // Montreal coordinates as ultimate fallback

    private defaultLocation: [number, number] = this.fallbackLocation;

    private currentCoordinates: [number, number] = this.fallbackLocation;

    private defaultZoom = 13;

    private userGeoEnabled = false;

    private userPosition: [number, number] | null = null;

    private mapInitAttempts = 0;

    private maxMapInitAttempts = 3;

    private resizeObserver: ResizeObserver | null = null;

    // --- Subscriptions ---
    private addressSubscription: Subscription | null = null;

    private profileSubscription: Subscription | null = null;

    private organisationSubscription: Subscription | null = null;

    private currentOrganisation: Organisation | null = null;

    constructor(
        private readonly zone: NgZone,
        private readonly translate: TranslateService,
        private readonly requestsV2Service: RequestsV2ImplService,
        private readonly store: Store,
        private readonly messageService: MessageService,
        private readonly cdRef: ChangeDetectorRef,
        private readonly geoidService: GeoidService,
    ) {
        // Setup address suggestions based on search input
        this.addressSuggestions = this.searchControl.valueChanges.pipe(
            startWith(''),
            filter((value) => typeof value === 'string' && value.trim().length > 3),
            debounceTime(700),
            distinctUntilChanged(),
            switchMap((value: string) => this.geoidService.searchAddressQuery(value)),
        );
    }

    // Function to format address for display in autocomplete
    displayAddressFn = (address: Address | null): string => {
        if (!address) return '';
        return this.formatAddress(address);
    };

    ngOnInit(): void {
        this.hasLocationSelected = false;
        this.selectionStateChanged.emit(false);
        this.getOrganisation();
        this.fetchProfileAndSetupOptions();
        if (this.previouslySelectedLocation) {
            this.currentlySelectedLocation = this.previouslySelectedLocation;
            this.hasLocationSelected = true;
            this.entireStreet = !!this.previouslySelectedLocation.entireStreet;
            this.selectedAddress = this.previouslySelectedLocation.type === 'custom' ? this.previouslySelectedLocation.addressData || this.parseAddress(this.previouslySelectedLocation.address) : null;
            this.selectionStateChanged.emit(true);
        }

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const lat = position.coords.latitude;
                    const lon = position.coords.longitude;
                    this.userGeoEnabled = true;
                    this.userPosition = [lat, lon];

                    // Set user position as default location
                    this.defaultLocation = [lat, lon];
                    this.currentCoordinates = [lat, lon];

                    // Update map if it's already initialized
                    if (this.map && this.marker) {
                        this.updateMapMarker([lat, lon]);
                    }

                    if (this.currentOrganisation && this.currentOrganisation.bounds && true) {
                        const actualOptionIndex = this.displayLocationOptions.findIndex((opt) => opt.type === 'actual');
                        if (actualOptionIndex === -1) {
                            const actualOption: LocationOption = {
                                id: 'actual',
                                type: 'actual' as const,
                                displayName: 'location-selection.location-options.actual',
                                address: this.translate.instant('location-selection.location-options.actual-loading'),
                                latitude: lat,
                                longitude: lon,
                                selected: false,
                                display: true,
                            };
                            this.displayLocationOptions.unshift(actualOption);
                            this.requestsV2Service.getAddressFromCoordinates(lat, lon).subscribe({
                                next: (addressData) => {
                                    const parsedAddress = this.parseAddress(addressData);
                                    actualOption.addressData = parsedAddress;
                                    actualOption.address = parsedAddress?.formattedAddress || `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
                                    this.cdRef.markForCheck();
                                },
                                error: () => {
                                    actualOption.address = this.translate.instant('location-selection.address-error');
                                    this.cdRef.markForCheck();
                                },
                            });
                        }
                    }
                },
                () => {
                    // If geolocation fails, we'll use the organization center from getOrganisation
                    console.log('Geolocation failed, using organization center');
                },
            );
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.initializeMap();

            // If returning to this step with a previously selected location, restore the map marker
            if (this.previouslySelectedLocation && this.previouslySelectedLocation.latitude !== null && this.previouslySelectedLocation.longitude !== null) {
                const coords: [number, number] = [this.previouslySelectedLocation.latitude, this.previouslySelectedLocation.longitude];
                this.currentCoordinates = coords;
                setTimeout(() => {
                    this.updateMapMarker(coords);
                    if (this.marker) this.marker.setOpacity(1);
                }, 500);
            }
        }, 300);
    }

    ngOnDestroy(): void {
        this.addressSubscription?.unsubscribe();
        this.profileSubscription?.unsubscribe();
        this.organisationSubscription?.unsubscribe();

        // Clean up resize observer
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        // Properly clean up Leaflet map and marker
        if (this.marker) {
            this.marker.remove();
            this.marker = null;
        }

        if (this.map) {
            // Remove all event listeners
            this.map.off();
            // Remove the map
            this.map.remove();
            this.map = null;
        }

        // Clear any references to DOM elements
        this.mapContainer = undefined;
    }

    private refreshMapSize(): void {
        if (this.map && this.mapInitialized) {
            this.map.invalidateSize();
        }
    }

    private getOrganisation(): void {
        this.organisationSubscription = (this.store as Store<State>)
            .select(organisationSelector)
            .pipe(take(1))
            .subscribe((organisation: Organisation) => {
                this.currentOrganisation = organisation;

                // Use organization's coordinates as default if available
                if (organisation && organisation.address && organisation.address.latitude && organisation.address.longitude) {
                    const orgLat = organisation.address.latitude;
                    const orgLon = organisation.address.longitude;

                    // If user hasn't shared location, use org center
                    if (!this.userGeoEnabled) {
                        this.defaultLocation = [orgLat, orgLon];
                        this.currentCoordinates = [orgLat, orgLon];
                    }
                }
            });
    }

    private fetchProfileAndSetupOptions(): void {
        this.profileSubscription = (this.store as Store<State>)
            .select(profileSelector)
            .pipe(take(1))
            .subscribe((profile: Profile | null) => {
                const baseOptions = this.locationOptions.map((opt) => ({ ...opt, selected: false }));

                const homeOptions: LocationOption[] = [];
                const homeAddress = profile?.addresses?.find((addr) => addr.isPrimary) || profile?.addresses?.[0];

                if (homeAddress && homeAddress.latitude != null && homeAddress.longitude != null) {
                    // Only include home address if city matches organization city
                    const orgCity = this.currentOrganisation?.address?.city;
                    const addressCity = homeAddress.city;

                    if (orgCity && addressCity && orgCity.toLowerCase() === addressCity.toLowerCase()) {
                        const parsedAddress = this.parseAddress(homeAddress);
                        const formattedAddress = parsedAddress?.formattedAddress || this.formatAddress(homeAddress);
                        homeOptions.push({
                            id: 'home',
                            type: 'home',
                            displayName: 'location-selection.location-options.home',
                            address: formattedAddress,
                            addressData: parsedAddress,
                            latitude: homeAddress.latitude,
                            longitude: homeAddress.longitude,
                            selected: false,
                            display: true,
                        });
                    }
                }

                // Always create a custom option for manual address
                // Only display if there's a previously selected location
                const customOption: LocationOption = {
                    id: 'manual',
                    type: 'custom',
                    displayName: 'location-selection.location-options.manual',
                    address: null,
                    latitude: null,
                    longitude: null,
                    selected: false,
                    display: false,
                };

                const noneOption: LocationOption = {
                    id: 'none',
                    type: 'none',
                    displayName: 'location-selection.location-options.none',
                    address: null,
                    latitude: null,
                    longitude: null,
                    selected: false,
                    display: true,
                };

                this.displayLocationOptions = [...homeOptions, ...baseOptions, customOption];

                if (this.selectedCategory.forceLocation === false) {
                    this.displayLocationOptions.push(noneOption);
                }

                // If there's a previously selected location, update the corresponding option
                if (this.previouslySelectedLocation) {
                    for (const option of this.displayLocationOptions) {
                        if (option.type === this.previouslySelectedLocation.type) {
                            // For custom/manual locations, update all properties
                            if (option.type === 'custom') {
                                const previousAddressData = this.previouslySelectedLocation.addressData || this.parseAddress(this.previouslySelectedLocation.address);

                                option.latitude = this.previouslySelectedLocation.latitude;
                                option.longitude = this.previouslySelectedLocation.longitude;
                                option.address = this.previouslySelectedLocation.address;
                                option.addressData = previousAddressData;
                                option.selected = true;
                                option.display = true;
                            } else if (option.id === this.previouslySelectedLocation.id) {
                                // For other location types, just mark as selected
                                option.selected = true;
                            }
                        }
                    }
                }

                this.cdRef.markForCheck();
            });
    }

    // Handle address selection from autocomplete
    addressSelected(address: Address): void {
        if (!address || !address.latitude || !address.longitude) {
            return;
        }

        // Check if the selected address city matches the organization city
        const orgCity = this.currentOrganisation?.address?.city;
        const addressCity = address.city;

        if (orgCity && addressCity && orgCity.toLowerCase() !== addressCity.toLowerCase()) {
            // Display error message if cities don't match
            this.messageService.error(
                this.translate.instant('location-selection.address-wrong-city', {
                    addressCity,
                    orgCity,
                }),
            );
            // Clear the search input when address is not in organization city
            this.searchControl.setValue('', { emitEvent: false });
            return;
        }

        /*
            const coords: [number, number] = [address.latitude, address.longitude];

       this.selectedAddress = this.parseAddress(address);

        this.updateMapMarker(coords);
        this.proceedWithSelectedLocation();

        setTimeout(() => {
            this.nextStep.emit();
        }, 300); */

        // Find the custom option for manual address
        const customOption = this.displayLocationOptions.find((opt) => opt.type === 'custom');
        if (customOption) {
            customOption.latitude = address.latitude;
            customOption.longitude = address.longitude;
            this.selectedAddress = this.parseAddress(address);
            const formattedAddress = this.selectedAddress?.formattedAddress || this.formatAddress(address);
            customOption.address = formattedAddress;
            customOption.addressData = this.selectedAddress;
            customOption.displayName = 'location-selection.location-options.manual';

            const coords: [number, number] = [address.latitude, address.longitude];
            this.updateMapMarker(coords);
            this.selectLocationOption(customOption);

            this.displayLocationOptions = this.displayLocationOptions.map((option) => {
                if (option.type === 'custom') {
                    option.display = true;
                }

                return option;
            });

            // Automatically proceed to next step after a short delay
            setTimeout(() => {
                this.proceedWithSelectedLocation();
                this.nextStep.emit();
            }, 50);
        }
    }

    // --- Rest of the component functionality ---
    private initializeMap(): void {
        if (this.mapInitialized || typeof L === 'undefined') return;

        const mapDiv = document.getElementById('map');
        if (!mapDiv) {
            this.handleMapError('Map container element not found.');
            return;
        }

        if (mapDiv.clientHeight < 10) {
            mapDiv.style.height = '220px';
        }

        this.mapLoading = true;
        this.cdRef.markForCheck();
        this.zone.runOutsideAngular(() => {
            try {
                this.map = L.map('map', {
                    center: this.defaultLocation as L.LatLngExpression,
                    zoom: this.defaultZoom,
                    zoomControl: false,
                    attributionControl: true,
                    fadeAnimation: true,
                    preferCanvas: true,
                    dragging: true,
                    doubleClickZoom: false,
                    scrollWheelZoom: false,
                    boxZoom: false,
                    keyboard: false,
                    tap: false,
                    touchZoom: false,
                });

                // Use a different tile provider that's more reliable
                L.tileLayer(MAP_TILE_URL, {
                    maxZoom: 19,
                    minZoom: 3,
                    attribution: MAP_TILE_ATTRIBUTION,
                }).addTo(this.map);

                // Create and add the marker with explicit coordinates
                this.marker = L.marker(this.defaultLocation as L.LatLngExpression, {
                    icon: createLocationMarkerIcon(),
                    zIndexOffset: 500,
                    opacity: 1,
                    draggable: false,
                }).addTo(this.map);

                // Add zoom control to the map
                L.control
                    .zoom({
                        position: 'topright',
                    })
                    .addTo(this.map);

                // Force map to render properly
                setTimeout(() => {
                    if (this.map) {
                        this.map.invalidateSize({ animate: false, debounceMoveend: true });
                        this.zone.run(() => {
                            this.mapInitialized = true;
                            this.mapLoading = false;
                            this.mapError = false;
                            this.cdRef.markForCheck();

                            // Ensure marker is visible after map is fully initialized
                            if (this.marker) {
                                this.marker.setOpacity(1);
                                this.marker.setLatLng(this.defaultLocation as L.LatLngExpression);
                            }
                        });
                    }
                }, 500);

                setTimeout(() => {
                    this.zone.run(() => {
                        if (this.mapLoading) {
                            this.mapLoading = false;
                            console.warn('Map loading timed out.');
                        }
                    });
                }, 3000);
            } catch (error) {
                console.error('Leaflet map initialization failed:', error);
                this.handleMapError('Failed to initialize map.');
            }
        });
    }

    private handleMapError(message: string): void {
        this.zone.run(() => {
            console.error('Map Error:', message);
            this.mapError = true;
            this.mapLoading = false;
            this.cdRef.markForCheck();
        });
    }

    private updateMapMarker(latLng: [number, number]): void {
        if (!this.map || !this.marker) {
            console.warn("Can't update marker, map or marker not initialized");
            return;
        }

        try {
            const newLatLng = L.latLng(latLng[0], latLng[1]);

            // Update marker position with explicit coordinates to ensure visibility
            this.marker.setLatLng(newLatLng);

            // Make sure marker is visible
            this.marker.setOpacity(1);

            // Center the map on the marker
            this.map.setView(newLatLng, this.map.getZoom());

            // Force a refresh to ensure marker and map tiles are properly displayed
            setTimeout(() => {
                if (this.map) {
                    this.map.invalidateSize({ animate: false });

                    // Re-add the marker if it's not visible
                    if (!this.marker || !this.marker.getLatLng()) {
                        this.marker = L.marker(newLatLng, {
                            icon: createLocationMarkerIcon(),
                            zIndexOffset: 500,
                            opacity: 1,
                        }).addTo(this.map);
                    }
                }
            }, 100);
        } catch (error) {
            console.error('Error updating map marker:', error);
        }
    }

    selectLocationOption(location: LocationOption): void {
        this.currentlySelectedLocation = location;
        this.selectedAddress = location.type === 'custom' ? location.addressData || this.selectedAddress : null;
        this.entireStreet = false;
        this.hasLocationSelected = true;
        this.selectionStateChanged.emit(true);
        this.displayLocationOptions.forEach((option) => {
            option.selected = option.id === location.id;
        });

        if (location.type === 'none') {
            if (this.marker) this.marker.setOpacity(0);
            this.currentCoordinates = [...this.defaultLocation];
        } else if (location.type === 'actual') {
            if (location.latitude != null && location.longitude != null) {
                const coords: [number, number] = [location.latitude, location.longitude];
                this.currentCoordinates = coords;
                this.updateMapMarker(coords);
                if (this.marker) this.marker.setOpacity(1);
            } else {
                if (this.marker) this.marker.setOpacity(0);
                this.currentCoordinates = [...this.defaultLocation];
            }
        } else if (location.type === 'home' || location.type === 'custom') {
            if (location.latitude != null && location.longitude != null) {
                const coords: [number, number] = [location.latitude, location.longitude];
                this.currentCoordinates = coords;
                this.updateMapMarker(coords);
                if (this.marker) this.marker.setOpacity(1);
            } else {
                if (this.marker) this.marker.setOpacity(0);
                this.currentCoordinates = [...this.defaultLocation];
            }
        } else {
            if (this.marker) this.marker.setOpacity(0);
            this.currentCoordinates = [...this.defaultLocation];
        }
        this.cdRef.markForCheck();
    }

    prepareLocationDto(): GeoJSONLocationRequest | null {
        if (!this.currentlySelectedLocation) {
            return null;
        }
        if (this.currentlySelectedLocation.type === 'none') {
            return null;
        }
        if (
            this.currentCoordinates[0] == null ||
            this.currentCoordinates[1] == null ||
            (this.currentCoordinates[0] === this.fallbackLocation[0] && this.currentCoordinates[1] === this.fallbackLocation[1] && this.currentlySelectedLocation.type !== 'home')
        ) {
            this.messageService.error(this.translate.instant('location-selection.internal-error-missing-coords'));
            return null;
        }
        const geometry: GeoJSONGeometry = {
            type: GeoJSONType.Point,
            coordinates: [this.currentCoordinates[1], this.currentCoordinates[0]],
        };
        let address: RequestLocationAddress | null = null;
        let { displayName } = this.currentlySelectedLocation;
        if (displayName && displayName.startsWith('location-selection.')) {
            displayName = this.translate.instant(displayName);
        }
        const selectedAddressData = this.currentlySelectedLocation.addressData || (this.currentlySelectedLocation.type === 'custom' ? this.selectedAddress : null);
        if (selectedAddressData) {
            address = selectedAddressData;
        } else if (this.currentlySelectedLocation.address) {
            address = this.parseAddress(this.currentlySelectedLocation.address) || {
                formattedAddress: this.currentlySelectedLocation.address,
            };
        } else {
            console.warn(`No address string found for selected option type: ${this.currentlySelectedLocation.type}. Using coordinates.`);
            address = {
                formattedAddress: `${this.currentCoordinates[0].toFixed(5)}, ${this.currentCoordinates[1].toFixed(5)}`,
            };
        }
        if (!address) {
            console.error('Address object is unexpectedly null during DTO preparation. Providing fallback.');
            address = {
                formattedAddress: this.translate.instant('location-selection.address-unavailable'),
            };
        }
        const locationRequest: GeoJSONLocationRequest = {
            type: 'Feature',
            properties: {
                name: displayName || 'Selected Location',
                type: this.currentlySelectedLocation.type,
                address,
            },
            geometry,
            entireStreet: this.entireStreet,
        };
        return locationRequest;
    }

    proceedWithSelectedLocation(): void {
        if (!this.hasLocationSelected || !this.currentlySelectedLocation) {
            this.messageService.error(this.translate.instant('location-selection.no-selection-proceed'));
            return;
        }
        if (this.currentlySelectedLocation.type === 'none') {
            this.onSkipLocation();
        } else {
            const dto = this.prepareLocationDto();
            if (dto) {
                this.currentlySelectedLocation.entireStreet = this.entireStreet;
                this.locationRequestData.emit(dto);
                this.locationSelected.emit(this.currentlySelectedLocation);
            } else {
                this.messageService.error(this.translate.instant('location-selection.dto-error-proceed'));
            }
        }
    }

    getSelectedLocation(): LocationOption | null {
        return this.currentlySelectedLocation;
    }

    getLocationRequestData(): GeoJSONLocationRequest | null {
        return this.prepareLocationDto();
    }

    onSkipLocation(): void {
        this.skipLocation.emit();
    }

    formatAddress(address: Address | RequestLocationAddress | null | undefined): string {
        if (!address) return '';
        const parts = [
            address.unit ? `${address.unit}-` : '',
            address.number,
            (address as Address).direction,
            address.streetName || address.street,
            (address as RequestLocationAddress).typeStreet || (address as Address).type,
            address.city,
            address.province,
            address.postCode,
        ];
        return parts
            .map((part) => (typeof part === 'string' ? part.trim() : part))
            .filter((part) => !!part)
            .join(' ')
            .replace(/ ,/g, ',')
            .replace(/ {2,}/g, ' ')
            .replace(/^ | $/g, '')
            .replace(/,(?=\S)/g, ', ');
    }

    private parseAddress(addressData: any): RequestLocationAddress | null {
        if (!addressData || typeof addressData !== 'object') {
            if (typeof addressData === 'string' && addressData.trim()) {
                return { formattedAddress: addressData };
            }
            return null;
        }

        const firstAddress = Array.isArray(addressData) ? addressData[0] : addressData;

        if (!firstAddress) return null;

        const formattedAddress =
            firstAddress.formattedAddress ||
            this.formatAddress(firstAddress) ||
            `${firstAddress.number || ''} ${firstAddress.streetName || firstAddress.street || ''}, ${firstAddress.city || ''}, ${firstAddress.province || ''} ${firstAddress.postCode || ''}`
                .replace(/ ,|, $/g, '')
                .trim();

        return {
            number: firstAddress.number != null ? `${firstAddress.number}` : '',
            street: this.toAddressString(firstAddress.street || firstAddress.streetName),
            streetName: this.toAddressString(firstAddress.streetName || firstAddress.street),
            city: this.toAddressString(firstAddress.city),
            province: this.toAddressString(firstAddress.province),
            postCode: this.toAddressString(firstAddress.postCode),
            country: firstAddress.country ? this.toAddressString(firstAddress.country) : undefined,
            typeStreet: this.toAddressString(firstAddress.typeStreet || firstAddress.type),
            direction: this.toAddressString(firstAddress.direction),
            unit: this.toAddressString(firstAddress.unit),
            formattedAddress,
        };
    }

    private toAddressString(value: unknown): string {
        return value == null ? '' : `${value}`;
    }

    refreshActualLocation(): void {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    this.zone.run(() => {
                        const latlng: [number, number] = [position.coords.latitude, position.coords.longitude];
                        this.currentCoordinates = latlng;
                        const [lat, lng] = latlng;

                        if (this.marker) {
                            this.marker.setLatLng(latlng);
                            this.marker.setOpacity(1);
                        }
                        if (this.map) {
                            this.map.setView(latlng, 16);
                        }
                        if (this.marker && this.currentlySelectedLocation?.type !== 'custom') {
                            this.marker.setOpacity(0);
                        }

                        const actualOption = this.displayLocationOptions.find((opt) => opt.type === 'actual');
                        if (actualOption) {
                            actualOption.latitude = lat;
                            actualOption.longitude = lng;
                            actualOption.address = this.translate.instant('location-options.actual-loading');
                            this.requestsV2Service.getAddressFromCoordinates(lat, lng).subscribe({
                                next: (addressData) => {
                                    const parsedAddress = this.parseAddress(addressData);
                                    actualOption.addressData = parsedAddress;
                                    actualOption.address = parsedAddress?.formattedAddress || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
                                    if (this.currentlySelectedLocation?.id === actualOption.id) {
                                        // If 'actual' is currently selected, update the map marker info potentially
                                        // This part might need review depending on desired UX when address resolves
                                    }
                                },
                                error: (err) => {
                                    console.error('Error fetching address for actual location:', err);
                                    actualOption.address = this.translate.instant('location-selection.address-error');
                                },
                            });

                            if (this.currentlySelectedLocation?.type === 'actual') {
                                this.selectLocationOption(actualOption);
                            }
                        } else {
                            console.error("Attempted to refresh 'actual' location, but option not found.");
                            this.messageService.error(this.translate.instant('location-selection.internal-error-no-actual'));
                        }
                    });
                },
                (error) => {
                    this.zone.run(() => {
                        console.error('Error getting location:', error);
                        const actualOption = this.displayLocationOptions.find((opt) => opt.type === 'actual');
                        if (actualOption) {
                            actualOption.address = this.translate.instant('location-selection.gps-error');
                        }
                        this.cdRef.markForCheck();
                        this.messageService.error(this.translate.instant('location-selection.gps-error-message'));
                    });
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
            );
        } else {
            console.error('Geolocation is not supported by this browser.');
            const actualOption = this.displayLocationOptions.find((opt) => opt.type === 'actual');
            if (actualOption) {
                actualOption.address = this.translate.instant('location-selection.gps-not-supported');
            }
            this.cdRef.markForCheck();
            this.messageService.error(this.translate.instant('location-selection.gps-not-supported-message'));
        }
    }

    private pointInBounds(lat: number, lon: number, bounds: any): boolean {
        if (!bounds || !bounds.northEast || !bounds.southWest) return false;
        return lat <= bounds.northEast.lat && lat >= bounds.southWest.lat && lon <= bounds.northEast.lon && lon >= bounds.southWest.lon;
    }
}
